import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  generateDepartmentCode, 
  getNextPriority,
  sortDepartmentsByPriority 
} from '../constants/departmentConstants';

const COLLECTIONS = {
  DEPARTMENTS: 'departments'
};

class DepartmentService {
  constructor() {
    this.collectionName = COLLECTIONS.DEPARTMENTS;
  }

  // Get all departments
  async getAllDepartments() {
    try {
      const deptRef = collection(db, this.collectionName);
      const q = query(deptRef, orderBy('sector', 'asc'), orderBy('priority', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const departments = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        departments.push({
          id: doc.id,
          ...data,
          // Ensure priority is a number
          priority: data.priority !== undefined && data.priority !== null && data.priority !== '' 
            ? Number(data.priority) 
            : null,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null
        });
      });
      
      return sortDepartmentsByPriority(departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw new Error('Failed to fetch departments');
    }
  }

  // Get active departments only
  async getActiveDepartments() {
    try {
      const allDepartments = await this.getAllDepartments();
      return allDepartments.filter(dept => dept.isActive);
    } catch (error) {
      console.error('Error fetching active departments:', error);
      throw new Error('Failed to fetch active departments');
    }
  }

  // Get departments by sector
  async getDepartmentsBySector(sector) {
    try {
      const deptRef = collection(db, this.collectionName);
      const q = query(
        deptRef, 
        where('sector', '==', sector),
        orderBy('priority', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      const departments = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        departments.push({
          id: doc.id,
          ...data,
          // Ensure priority is a number
          priority: data.priority !== undefined && data.priority !== null && data.priority !== '' 
            ? Number(data.priority) 
            : null,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null
        });
      });
      
      return departments;
    } catch (error) {
      console.error('Error fetching departments by sector:', error);
      throw new Error('Failed to fetch departments for sector');
    }
  }

  // Get single department by ID
  async getDepartmentById(id) {
    try {
      const deptRef = doc(db, this.collectionName, id);
      const deptSnap = await getDoc(deptRef);
      
      if (deptSnap.exists()) {
        const data = deptSnap.data();
        return {
          id: deptSnap.id,
          ...data,
          // Ensure priority is a number
          priority: data.priority !== undefined && data.priority !== null && data.priority !== '' 
            ? Number(data.priority) 
            : null,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null
        };
      }
      
      throw new Error('Department not found');
    } catch (error) {
      console.error('Error fetching department:', error);
      throw new Error('Failed to fetch department');
    }
  }

  // Get department by code
  async getDepartmentByCode(code) {
    try {
      const deptRef = collection(db, this.collectionName);
      const q = query(deptRef, where('departmentCode', '==', code));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure priority is a number
          priority: data.priority !== undefined && data.priority !== null && data.priority !== '' 
            ? Number(data.priority) 
            : null,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching department by code:', error);
      throw new Error('Failed to fetch department');
    }
  }

  // Create new department
  async createDepartment(departmentData, userId) {
    try {
      // Generate department code if not provided
      const departmentCode = departmentData.departmentCode || 
                            generateDepartmentCode(departmentData.departmentName);
      
      // Check if code already exists
      const existingDept = await this.getDepartmentByCode(departmentCode);
      if (existingDept) {
        throw new Error('Department code already exists');
      }

      // Get next priority if not provided
      const allDepartments = await this.getAllDepartments();
      const priority = departmentData.priority || 
                      getNextPriority(departmentData.sector, allDepartments);

      const deptRef = doc(collection(db, this.collectionName));
      const newDepartment = {
        sector: departmentData.sector || '',
        departmentName: departmentData.departmentName || '',
        departmentCode: departmentCode,
        priority: Number(priority), // Ensure it's stored as a number
        isActive: departmentData.isActive !== undefined ? departmentData.isActive : true,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(deptRef, newDepartment);
      
      return {
        id: deptRef.id,
        ...newDepartment,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error creating department:', error);
      throw error;
    }
  }

  // Update department
  async updateDepartment(id, updateData, userId) {
    try {
      const deptRef = doc(db, this.collectionName, id);
      
      // If department name is being updated, regenerate code
      if (updateData.departmentName) {
        const newCode = generateDepartmentCode(updateData.departmentName);
        
        // Check if new code conflicts with existing departments (except current)
        const existingDept = await this.getDepartmentByCode(newCode);
        if (existingDept && existingDept.id !== id) {
          throw new Error('Department code already exists');
        }
        
        updateData.departmentCode = newCode;
      }

      // Ensure priority is stored as a number
      if (updateData.priority !== undefined && updateData.priority !== null && updateData.priority !== '') {
        updateData.priority = Number(updateData.priority);
      }

      const updates = {
        ...updateData,
        updatedBy: userId,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(deptRef, updates);
      
      return {
        id,
        ...updates,
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error updating department:', error);
      throw error;
    }
  }

  // Delete department
  async deleteDepartment(id) {
    try {
      const deptRef = doc(db, this.collectionName, id);
      await deleteDoc(deptRef);
      
      console.log('Department deleted successfully');
    } catch (error) {
      console.error('Error deleting department:', error);
      throw new Error('Failed to delete department');
    }
  }

  // Toggle department status
  async toggleDepartmentStatus(id, userId) {
    try {
      const department = await this.getDepartmentById(id);
      const newStatus = !department.isActive;
      
      await this.updateDepartment(id, { isActive: newStatus }, userId);
      
      return newStatus;
    } catch (error) {
      console.error('Error toggling department status:', error);
      throw new Error('Failed to toggle department status');
    }
  }

  // Validate department data
  validateDepartment(departmentData) {
    const errors = {};
    
    if (!departmentData.sector || !departmentData.sector.trim()) {
      errors.sector = 'Sector is required';
    }
    
    if (!departmentData.departmentName || !departmentData.departmentName.trim()) {
      errors.departmentName = 'Department name is required';
    } else if (departmentData.departmentName.trim().length < 2) {
      errors.departmentName = 'Department name must be at least 2 characters';
    }
    
    if (departmentData.priority !== undefined && departmentData.priority !== null && departmentData.priority !== '') {
      const priority = Number(departmentData.priority);
      if (isNaN(priority) || priority < 1 || priority > 999) {
        errors.priority = 'Priority must be between 1 and 999';
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Reorder departments within a sector
  async reorderDepartments(sector, departmentIds, userId) {
    try {
      const updates = departmentIds.map((id, index) => 
        this.updateDepartment(id, { priority: index + 1 }, userId)
      );
      
      await Promise.all(updates);
      
      console.log('Departments reordered successfully');
    } catch (error) {
      console.error('Error reordering departments:', error);
      throw new Error('Failed to reorder departments');
    }
  }

  // Get department label by code
  getDepartmentLabel(departmentCode, departments) {
    const dept = departments.find(d => d.departmentCode === departmentCode);
    return dept ? dept.departmentName : departmentCode;
  }

  // Get departments for dropdown options
  getDepartmentOptions(departments, sector = null, includeInactive = false) {
    let filteredDepts = departments;
    
    // Filter by sector if provided
    if (sector) {
      filteredDepts = filteredDepts.filter(d => d.sector === sector);
    }
    
    // Filter by active status
    if (!includeInactive) {
      filteredDepts = filteredDepts.filter(d => d.isActive);
    }
    
    // Sort by priority
    filteredDepts = sortDepartmentsByPriority(filteredDepts);
    
    // Convert to options format
    return filteredDepts.map(dept => ({
      value: dept.departmentCode,
      label: dept.departmentName,
      priority: dept.priority,
      sector: dept.sector
    }));
  }
}

export const departmentService = new DepartmentService();